import type { Person } from "@/lib/person"

// Props of Push/Review, as Push::ReviewsController sends them.

export interface Group {
  id: string // a product group id, or "other"
  name: string
}

// One product on the High DOC list.
export interface ReviewRow {
  external_id: string
  name: string
  short_name: string | null
  group_id: string
  stock: number
  doc: number | null // days of cover; null when nothing went out in 90 days
  value?: number | null // stock × buying price; left out without the gross margin permission, null when unknown
  incoming: IncomingSummary | null // dated incoming orders; null when none or nothing goes out
  excess: Excess
}

// The stock on hand that won't sell within the cut-off at today's pace
// (Push::Review::Board). Listed products always have a known value.
export interface Excess {
  units: number
  value: number
}

// The dated incoming orders of a listed product: units, when the last one is
// due, and the days of cover once it has landed.
export interface IncomingSummary {
  quantity: number
  last_arrives_on: string
  doc: number
}

export interface Site {
  warehouse: string
  qty: number
  out_per_day: number // average units out per day over 90 days (fewer for a new product), sold plus gifted
}

export interface IncomingLine {
  quantity: number
  arrives_on: string | null // ISO date; null when the order has no ETA
  order_no: string | null
  status: string
  lasts_days: number | null // how long stock lasts once it lands; null when unknown or nothing moves
}

export interface Detail {
  external_id: string
  name: string
  short_name: string | null
  group_name: string | null
  sites: Site[]
  incoming: IncomingLine[]
  out_per_day: number
  days_on_sale: number | null // set for a new product: its pace counts only these days, not 90
  stock: number
  today: string // ISO date, for marking incoming orders past due
}

export interface ReviewAction {
  id: string
  product_external_id: string
  action_type: string
  note: string
  channel_id: string | null // null means all channels
  channel_name: string | null
  starts_on: string | null
  ends_on: string | null
  assignee_ids: string[]
  created_by: string
  created_on: string
}

export interface Channel {
  id: string
  name: string
}

export interface ReviewProps {
  cutoff: { value: number; default: number; min: number; max: number }
  // In the market's currency.
  min_excess_value: { value: number; default: number }
  groups: Group[]
  rows: ReviewRow[]
  action_counts: Record<string, number>
  can_see_value: boolean
  action_types: string[]
  channels: Channel[]
  people: Person[]
  selected: string | null
  detail: Detail | null
  actions: ReviewAction[]
  mentioned_by: ReviewAction[]
  mention_names: MentionNames
}

// The name for the list: the short name when there is one, otherwise the
// full name without leading tags such as "[CO-BRAND]".
export const listName = (p: { name: string; short_name: string | null }) =>
  p.short_name ?? p.name.replace(/^\s*(\[[^\]]*\]\s*)+/, "").trim()

// { product id => its short name and full name } for every #id in the notes shown.
export type MentionNames = Record<string, { short_name: string | null; name: string }>
